/**
 * Playback store: transport state shared between the preview engine, the
 * timeline (playhead) and the top bar. The engine writes currentTime each
 * animation frame; components subscribe narrowly.
 */
import { create } from 'zustand';

/**
 * Loop region over timeline time. Either bound may be unset while the user is
 * still marking (I sets in, O sets out — mirroring the clip-viewer marking
 * semantics); the engine only wraps when both are set and out > in.
 */
export interface LoopRegion {
  in: number | null;
  out: number | null;
}

interface PlaybackState {
  currentTime: number;
  playing: boolean;
  /** loop region (I/O keys); null = no loop */
  loop: LoopRegion | null;
  /**
   * Source ids whose live <video> isn't ready and are being shown via a pandora
   * frame fallback (or nothing). Non-empty ⇒ the preview is showing a preview
   * frame / loading indicator instead of the real decoded video.
   */
  bufferingSources: string[];
  setTime(t: number): void;
  setPlaying(playing: boolean): void;
  setBuffering(sourceIds: string[]): void;
  /** Set the loop in point; an out point at or before it is cleared. */
  setLoopIn(t: number): void;
  /** Set the loop out point; an in point at or after it is cleared. */
  setLoopOut(t: number): void;
  clearLoop(): void;
}

export const usePlayback = create<PlaybackState>((set) => ({
  currentTime: 0,
  playing: false,
  loop: null,
  bufferingSources: [],
  setTime(t) {
    set({ currentTime: Math.max(0, t) });
  },
  setPlaying(playing) {
    set({ playing });
  },
  setBuffering(sourceIds) {
    set({ bufferingSources: sourceIds });
  },
  setLoopIn(t) {
    set((s) => {
      const out = s.loop?.out ?? null;
      return { loop: { in: t, out: out !== null && out <= t ? null : out } };
    });
  },
  setLoopOut(t) {
    set((s) => {
      const inPoint = s.loop?.in ?? null;
      return { loop: { in: inPoint !== null && inPoint >= t ? null : inPoint, out: t } };
    });
  },
  clearLoop() {
    set({ loop: null });
  },
}));
